import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from '@phosphor-icons/react';
import { motion } from '@/lib/motion';
import axios from 'axios';
import { getSrcSet, SIZES } from '../utils/responsiveImage';
import { resolveMediaUrl, getGeneratedImage } from '../utils/mediaUrl';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback industries if API fails
const fallbackIndustries = [
  { id: 'architects', slug: 'architects', title: 'For Architects', image: getGeneratedImage('industry_architects') },
  { id: 'interior-designers', slug: 'interior-designers', title: 'For Interior Designers', image: getGeneratedImage('industry_interior') },
  { id: 'hospitality', slug: 'hospitality', title: 'Hospitality', image: getGeneratedImage('industry_hospitality') },
  { id: 'commercial', slug: 'commercial', title: 'Commercial', image: getGeneratedImage('industry_commercial') },
  { id: 'residential', slug: 'residential', title: 'Residential', image: getGeneratedImage('industry_residential') },
  { id: 'retail', slug: 'retail', title: 'Retail & F&B', image: getGeneratedImage('industry_retail') },
  { id: 'contractors', slug: 'contractors', title: 'For Contractors', image: getGeneratedImage('industry_contractors') },
  { id: 'developers', slug: 'developers', title: 'For Developers', image: getGeneratedImage('industry_developers') },
  { id: 'art-consultants', slug: 'art-consultants', title: 'Art Consultants & Artists', image: getGeneratedImage('industry_art') },
  { id: 'cultural', slug: 'cultural', title: 'Cultural & Religious', image: getGeneratedImage('industry_cultural') },
];

// Helper to get full image URL
const getImageUrl = (path) => {
  if (!path) return getGeneratedImage('industry_architects');
  return resolveMediaUrl(path);
};

export const IndustrySection = () => {
  const [industries, setIndustries] = useState(fallbackIndustries);
  
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await axios.get(`${API}/api/industries`);
        if (response.data?.length > 0) {
          // Map API data to component format with proper image URLs
          const mappedIndustries = response.data.map(ind => ({
            id: ind.slug || ind.id,
            slug: ind.slug,
            title: ind.title,
            image: getImageUrl(ind.image || ind.heroImage),
          }));
          setIndustries(mappedIndustries);
        }
      } catch (error) {
        console.log('Using fallback industries data');
      }
    };
    fetchIndustries();
  }, []);
  return (
    <section id="industries" className="py-16 md:py-20 bg-white" data-testid="industry-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header - Centered */}
        <div className="text-center mb-10">
          <span className="label-architectural text-bronze">WHO WE SERVE</span>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-obsidian mt-2">
            INDUSTRIES & CLIENTS
          </h2>
          <p className="font-body text-concrete text-sm mt-2 max-w-xl mx-auto">
            Partnering with design professionals and serving diverse market sectors across the UAE and GCC
          </p>
        </div>

        {/* Industries Grid - 5 columns on desktop, 2 on mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
          {industries.map((industry, index) => (
            <motion.div
              key={industry.id}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05, duration: 0.4 }}
            >
              <Link
                to={`/industries/${industry.slug}`}
                className="group relative block aspect-[3/4] overflow-hidden"
                data-testid={`industry-card-${index}`}
              >
                <img loading="lazy" decoding="async"
                  src={industry.image} 
                  srcSet={getSrcSet(industry.image)}
                  sizes={SIZES.fourCol}
                  alt={industry.title}
                  width={600}
                  height={800}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-obsidian/20 to-transparent" />
                <div className="absolute inset-0 p-3 md:p-4 flex flex-col justify-end">
                  <h3 className="font-display text-[10px] md:text-xs font-bold text-white group-hover:text-bronze transition-colors uppercase tracking-wide">
                    {industry.title}
                  </h3>
                </div>
                <div className="absolute top-2 right-2 w-6 h-6 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity group-hover:bg-white">
                  <ArrowUpRight weight="bold" className="w-3 h-3 text-white group-hover:text-obsidian" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustrySection;
